import React, { ReactNode, useState } from 'react';
import {
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	IconProps,
} from '@drift-labs/icons';
import { useDebouncedCallback } from 'use-debounce';
import { notify } from 'src/utils/notifications';
import PaginationInputPopup from './Popups/PaginationInputPopup';
import useTargetedPopover from 'src/hooks/useTargetedPopover';
import { getPageNumForCount } from 'src/utils/pagination';
import { useDebounce } from 'react-use';
import { cm } from 'src/utils/classNames';
import SelectComponent from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

export interface PaginatorProps extends WithClassName {
	pageIndex: number;
	pageSize: number;
	onPageChange: (pageIndex: number) => void;
	totalCount: number;
	noBorder?: boolean;
	/**
	 * Rendered to the left of the paginator on desktop and below on mobile.
	 */
	message?: ReactNode;
	fixedMaxPageIndex?: number;
	/**
	 * Token-based pagination allows the API server to keep track of where to continue fetching the next set of records.
	 * This means it can only go 1 page forward max, and can go backwards to any page that has been fetched (and token stored).
	 */
	isTokenBasedPagination?: boolean;
	disableRateLimitNotification?: boolean;
	/**
	 * Items per page functionality.
	 */
	pageSizeOptions?: number[];
	onPageSizeChange?: (newPageSize: number) => void;
	showItemsPerPage?: boolean;
	/**
	 * Show both items per page and message on desktop.
	 */
	showBothLeftContent?: boolean;
	/**
	 * Hide the double arrow button (last page button).
	 */
	hideLastPageButton?: boolean;
	/**
	 *	Number of page options to show for token-based pagination.
	 */
	tokenBasedPaginationPageCount?: number;
}

const Paginator = (props: PaginatorProps) => {
	const maxPages =
		props.fixedMaxPageIndex ??
		getPageNumForCount(props.totalCount, props.pageSize);
	const maxPageIndex = maxPages - 1;

	const [firstPageIndexShown, setFirstPageIndexShown] = useState(0);
	const [showFromLast, setShowFromLast] = useState(false);
	const [debouncedCurrentPageIndex, setDebouncedCurrentPageIndex] = useState(
		props.pageIndex
	);

	const {
		value: pageSizeValue,
		open: pageSizeOpen,
		onOpenChange: onPageSizeOpenChange,
		onValueChange: onPageSizeValueChange,
		selectedOption: selectedPageSizeOption,
		selectOptions: pageSizeSelectOptions,
	} = useSelect({
		selectedValue: props.pageSize.toString(),
		onSelectedValueChange: (value: string) => {
			props.onPageSizeChange?.(parseInt(value));
		},
		options:
			props.pageSizeOptions?.map((size) => ({
				value: size.toString(),
				label: size.toString(),
			})) ?? [],
	});

	const {
		refs,
		floatingStyles,
		getReferenceProps,
		getFloatingProps,
		setIsPopoverOpen: setShowPaginationInput,
		isPopoverOpen: showPaginationInput,
	} = useTargetedPopover(
		{
			placement: 'top',
		},
		{
			offset: 16,
			disableAutoPlacement: true,
		}
	);

	const debouncedExecutePageChange = useDebouncedCallback(
		(newPage: number) => {
			if (newPage < 0) {
				setShowFromLast(true);
			}

			props.onPageChange(newPage);
		},
		500,
		{ leading: true }
	);

	const handlePageChange = (newPage: number) => {
		if (
			debouncedExecutePageChange.isPending() &&
			!props.disableRateLimitNotification
		) {
			notify({
				id: 'paginationRateLimit',
				type: 'info',
				message: 'Too many requests',
				description:
					"You can jump directly to a history page by clicking the '...' button",
			});
		}
		debouncedExecutePageChange(newPage);
	};

	useDebounce(
		// use debounce is to ensure both the page numbers in the paginator and the selected page number are updated at the same time
		() => {
			setDebouncedCurrentPageIndex(props.pageIndex);

			if (props.isTokenBasedPagination) {
				const pageCount = props.tokenBasedPaginationPageCount ?? 2; // Default to 2 pages
				const halfPageCount = Math.floor(pageCount / 2);

				if (props.pageIndex >= halfPageCount) {
					setFirstPageIndexShown(Math.max(0, props.pageIndex - halfPageCount));
				} else {
					setFirstPageIndexShown(0);
				}
			} else {
				const pageNumbersDisplayedInPaginator = [
					firstPageIndexShown,
					firstPageIndexShown + 1,
					firstPageIndexShown + 2,
				];

				if (
					(!pageNumbersDisplayedInPaginator.includes(props.pageIndex) ||
						firstPageIndexShown > props.pageIndex) &&
					props.pageIndex !== maxPages
				) {
					setFirstPageIndexShown(props.pageIndex);
				}
			}
		},
		10,
		[
			firstPageIndexShown,
			props.pageIndex,
			maxPageIndex,
			props.isTokenBasedPagination,
		]
	);

	return (
		<>
			<div
				className={cm(
					'flex items-center justify-center px-1 py-md sm:bg-container-bg md:justify-end md:px-4',
					'typo-t4 text-text-secondary',
					!props.noBorder && 'border-t border-stroke-divider',
					props.className
				)}
			>
				{props.showBothLeftContent ? (
					// Show both items per page and message.
					<div className="hidden items-center space-x-6 md:flex">
						{props.showItemsPerPage &&
							props.pageSizeOptions &&
							props.onPageSizeChange && (
								<div className="w-32">
									<SelectComponent.Root
										open={pageSizeOpen}
										onOpenChange={onPageSizeOpenChange}
										value={pageSizeValue}
										onValueChange={onPageSizeValueChange}
									>
										<SelectComponent.Trigger
											className="w-full"
											label="Items per page"
										>
											{selectedPageSizeOption?.label ?? 'Select...'}
										</SelectComponent.Trigger>
										<SelectComponent.Options>
											{pageSizeSelectOptions.map((option) => (
												<SelectComponent.Option
													key={option.value}
													value={option.value}
												>
													{option.label}
												</SelectComponent.Option>
											))}
										</SelectComponent.Options>
									</SelectComponent.Root>
								</div>
							)}

						{props.message && (
							<div className="hidden w-full md:flex">{props.message}</div>
						)}
					</div>
				) : props.showItemsPerPage &&
				  props.pageSizeOptions &&
				  props.onPageSizeChange ? (
					<div className="hidden items-center space-x-2 bg-container-bg md:flex">
						<div className="w-32">
							<SelectComponent.Root
								open={pageSizeOpen}
								onOpenChange={onPageSizeOpenChange}
								value={pageSizeValue}
								onValueChange={onPageSizeValueChange}
							>
								<SelectComponent.Trigger className="h-8 w-full text-xs whitespace-nowrap">
									<div className="flex items-center">
										<span className="mr-2 font-light text-text-secondary">
											Items per page
										</span>
										<span>{selectedPageSizeOption?.label ?? 'Select...'}</span>
									</div>
								</SelectComponent.Trigger>
								<SelectComponent.Options>
									{pageSizeSelectOptions.map((option) => (
										<SelectComponent.Option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</SelectComponent.Option>
									))}
								</SelectComponent.Options>
							</SelectComponent.Root>
						</div>
					</div>
				) : (
					props.message && (
						<span className="hidden w-full md:flex">{props.message}</span>
					)
				)}
				{maxPages > 1 && (
					<div className="my-1 mr-0 flex h-2xl items-center md:mx-4">
						{/** Back to first page */}
						<PaginatorIconButton
							icon={ChevronsLeft}
							disabled={props.pageIndex == 0}
							onClick={(e) => {
								if (props.pageIndex == 0) return;
								e?.stopPropagation();
								handlePageChange(0);
							}}
						/>

						{/** Previous page */}
						<PaginatorIconButton
							icon={ChevronLeft}
							disabled={props.pageIndex == 0}
							onClick={(e) => {
								if (props.pageIndex == 0) return;
								e?.stopPropagation();
								handlePageChange(Math.max(0, props.pageIndex - 1));
							}}
						/>

						{/** Dynamic Page Number Buttons */}
						{(() => {
							const pageCount = props.isTokenBasedPagination
								? (props.tokenBasedPaginationPageCount ?? 2)
								: 3;
							const buttons = [];

							for (let i = 0; i < pageCount; i++) {
								const pageIndex = firstPageIndexShown + i;
								const isVisible = props.isTokenBasedPagination
									? pageIndex <= maxPageIndex
									: i < 2 ||
										(i === 2 &&
											debouncedCurrentPageIndex < maxPageIndex &&
											maxPageIndex > 1);

								if (isVisible && pageIndex <= maxPageIndex) {
									const isSelected = debouncedCurrentPageIndex === pageIndex;
									buttons.push(
										<PaginatorButton
											key={pageIndex}
											isSelected={isSelected}
											onClick={(e) => {
												e?.stopPropagation();
												handlePageChange(pageIndex);
											}}
										>
											{pageIndex + 1}
										</PaginatorButton>
									);
								}
							}

							return buttons;
						})()}

						{/** Ellipsis Button for manual page input */}
						{maxPages >= 6 && !props.isTokenBasedPagination && (
							<PaginatorButton
								onClick={(e) => {
									e?.stopPropagation();
									setShowPaginationInput(true);
								}}
							>
								<div
									ref={refs.setReference}
									{...getReferenceProps()}
									className="select-none hover:cursor-pointer"
								>
									...
								</div>
							</PaginatorButton>
						)}

						{showFromLast && (
							<>
								{maxPageIndex > firstPageIndexShown + 3 && (
									<PaginatorButton
										isSelected={props.pageIndex === maxPages - 2}
										onClick={(e) => {
											e?.stopPropagation();
											handlePageChange(maxPages - 2);
										}}
									>
										{maxPages - 1}
									</PaginatorButton>
								)}
								{maxPages > firstPageIndexShown + 3 && (
									<PaginatorButton
										isSelected={props.pageIndex === maxPages - 1}
										onClick={(e) => {
											e?.stopPropagation();
											handlePageChange(maxPages - 1);
										}}
									>
										{maxPages}
									</PaginatorButton>
								)}
								{maxPages + 1 > firstPageIndexShown + 3 && (
									<PaginatorButton
										isSelected={props.pageIndex === maxPages}
										onClick={(e) => {
											e?.stopPropagation();
											handlePageChange(maxPages);
										}}
									>
										{maxPages + 1}
									</PaginatorButton>
								)}
							</>
						)}

						{/** Next page */}
						<PaginatorIconButton
							icon={ChevronRight}
							disabled={props.pageIndex >= maxPageIndex}
							className={cm(props.hideLastPageButton && 'mr-0 pr-0')}
							onClick={(e) => {
								if (props.pageIndex >= maxPageIndex) return;
								e?.stopPropagation();
								handlePageChange(Math.min(maxPages, props.pageIndex + 1));
							}}
						/>

						{/** To last page */}
						{!props.isTokenBasedPagination && !props.hideLastPageButton && (
							<PaginatorIconButton
								icon={ChevronsRight}
								disabled={props.pageIndex >= maxPageIndex}
								onClick={(e) => {
									if (props.pageIndex >= maxPageIndex) return;
									e?.stopPropagation();
									handlePageChange(-1);
								}}
							/>
						)}
					</div>
				)}
			</div>

			{props.message && (
				<div className="typo-t4 text-text-secondary md:hidden">
					{props.message}
				</div>
			)}

			{showPaginationInput && (
				<PaginationInputPopup
					setFloating={refs.setFloating}
					floatingStyles={floatingStyles}
					getFloatingProps={getFloatingProps}
					initialValue={props.pageIndex + 1}
					maxValue={maxPages + 1}
					onClose={(newVal: number) => {
						setShowPaginationInput(false);

						if (newVal - 1 < 0) return;
						if (newVal - 1 > maxPages) return;

						if (newVal - 1 !== props.pageIndex) handlePageChange(newVal - 1);
					}}
				/>
			)}
		</>
	);
};

interface PaginatorButtonProps extends WithClassName, WithChildren {
	isSelected?: boolean;
	onClick: (e: React.MouseEvent) => void;
}

const PaginatorButton = ({
	isSelected,
	onClick,
	children,
	className,
}: PaginatorButtonProps) => (
	<Button
		size="sm"
		variant="secondary"
		className={cm(
			'mx-xs h-2xl min-w-2xl p-xs',
			isSelected
				? 'bg-button-secondary-bg text-text-default'
				: 'bg-transparent text-text-secondary hover:bg-purple-bg',
			className
		)}
		onClick={onClick}
	>
		{children}
	</Button>
);

interface PaginatorIconButtonProps extends WithClassName {
	icon: React.ComponentType<IconProps>;
	disabled?: boolean;
	onClick: (e: React.MouseEvent) => void;
}

const PaginatorIconButton = ({
	icon,
	disabled,
	onClick,
	className,
}: PaginatorIconButtonProps) => (
	<IconButton
		size="sm"
		variant="secondary"
		className={cm(
			'mx-xs bg-transparent p-xs text-text-secondary hover:bg-purple-bg disabled:bg-transparent',
			className
		)}
		icon={icon}
		disabled={disabled}
		onClick={onClick}
	/>
);

export default Paginator;
